// Описание карточки: типы, на которых держится весь конструктор.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ. Разделов в CRM сорок с лишним, и все они сегодня — списки на чтение.
// Карточка (сделка, компания, судно, договор, счёт, акт, проверка) — единица работы, а не
// строка списка: её заполняют, у неё есть состояние, её правки надо помнить. Написать её
// один раз для сделки и повторить восемь раз копированием значит получить восемь разных
// «обязательное поле», восемь способов показать «изменено, но не сохранено» и восемь мест,
// где забыли подсказку. Поэтому карточка описывается ДАННЫМИ, а рисуется одним кодом.
//
// ЧТО ЗДЕСЬ ЕСТЬ. Только типы и словари подписей — ни одной строки разметки и ни одного
// запроса. Так описание сущности (`specs/*.ts`) можно читать и править, не открывая React,
// а проверки (`validate.ts`) работают без отрисовки.
//
// ОТКУДА ВЗЯТЫ ВИДЫ ПОЛЕЙ И СОСТОЯНИЯ. Из `cir-core/МОДЕЛЬ_ДАННЫХ.md` §5: справочные
// сущности живут в `state` (draft/active/archived), документы — в `doc_state`
// (draft/issued/cancelled), деньги никогда не строка (сумма + валюта отдельно), связь —
// внешний ключ, «нет связи» — NULL, а не `'0'` и не пустая строка.

// ─────────────────────────── что бывает в карточке ───────────────────────────

/**
 * Вид поля. Девять видов, больше пока не понадобилось.
 *
 * `текст` — длинный текст (условия оплаты, заключение акта): многострочный ввод.
 * `связь` — ссылка на другую карточку, выбирается поиском, а не из выпадающего списка:
 * сущностей десятки тысяч, список целиком не загрузить.
 */
export type ВидПоля =
  | 'строка'
  | 'текст'
  | 'число'
  | 'деньги'
  | 'дата'
  | 'список'
  | 'флаг'
  | 'связь'
  | 'файл'

/** Сущности, у которых есть карточка. Код совпадает с именем таблицы своей схемы. */
export type Сущность =
  | 'сделка'
  | 'компания'
  | 'контакт'
  | 'судно'
  | 'сотрудник'
  | 'договор'
  | 'счёт'
  | 'акт'
  | 'проверка'

/** Вкладки карточки. Порядок задан здесь: он одинаков у всех сущностей. */
export type КодВкладки = 'основное' | 'связи' | 'документы' | 'история' | 'лента'

export const ВКЛАДКИ: { код: КодВкладки; подпись: string }[] = [
  { код: 'основное', подпись: 'Основное' },
  { код: 'связи', подпись: 'Связи' },
  { код: 'документы', подпись: 'Документы' },
  { код: 'история', подпись: 'История изменений' },
  { код: 'лента', подпись: 'Лента событий' },
]

// ─────────────────────────── состояния ───────────────────────────

/** Состояние документа: сделка, договор, счёт, акт, проверка. */
export type СостояниеДокумента = 'draft' | 'issued' | 'cancelled'

/** Состояние справочной записи: компания, контакт, судно, сотрудник. */
export type СостояниеСправочника = 'draft' | 'active' | 'archived'

export type Состояние = СостояниеДокумента | СостояниеСправочника

export const ПОДПИСЬ_СОСТОЯНИЯ: Record<Состояние, string> = {
  draft: 'Черновик',
  issued: 'Выпущен',
  cancelled: 'Аннулирован',
  active: 'Действует',
  archived: 'В архиве',
}

/**
 * Класс значка состояния. `.badge.won` — зелёный, `.badge.lose` — красный,
 * `.badge.work` — синий; классы уже есть в styles.css, свои заводить незачем.
 */
export const ЗНАЧОК_СОСТОЯНИЯ: Record<Состояние, string> = {
  draft: 'badge',
  issued: 'badge won',
  cancelled: 'badge lose',
  active: 'badge won',
  archived: 'badge',
}

// ─────────────────────────── значения полей ───────────────────────────

/**
 * Деньги. Сумма и валюта врозь — как в схеме и по той же причине: `"515000|RUB"` одной
 * строкой уже стоило нам разбора в трёх местах, а сложить две такие строки нельзя вовсе.
 */
export interface Деньги {
  сумма: number | null
  валюта: string
}

/** Ссылка на другую карточку: то, что показывается в поле-связи и в списке поиска. */
export interface Ярлык {
  сущность: Сущность
  id: string
  название: string
  /** Вторая строка в списке поиска: ИНН, IMO, стадия — чем различать тёзок. */
  подсказка?: string
}

/** Файл, прикреплённый к карточке. */
export interface Файл {
  id: string
  имя: string
  /** Байты. Ноль — это ноль, а не «неизвестно»: неизвестного размера у файла не бывает. */
  размер: number
  /** Роль вложения: «скан акта», «фото отгрузки». */
  роль?: string
  добавил?: string
  когда?: string
}

export type Значение =
  | string
  | number
  | boolean
  | null
  | undefined
  | Деньги
  | Ярлык
  | Файл[]

/** Значения карточки: код поля → значение. */
export type Значения = Record<string, Значение>

/** Вариант списка. Код строковый и осмысленный — числовые enum_id Bitrix не переносим. */
export interface Вариант {
  код: string
  подпись: string
  /** Пояснение к варианту: показывается подсказкой под полем после выбора. */
  подсказка?: string
}

// ─────────────────────────── описание поля ───────────────────────────

/**
 * Автоподстановка из связанной карточки.
 *
 * Владелец просил отдельно: заполнять сделку должно быть легко. Половина «легко» — это
 * не печатать то, что система уже знает: выбрал компанию — подтянулись наша компания,
 * ответственный менеджер и условия оплаты из последнего договора.
 *
 * Подставленное значение ПОМЕЧАЕТСЯ на экране и остаётся правимым: подстановка — это
 * заготовка, а не решение за человека.
 */
export interface Подстановка {
  /** Код поля-связи, из карточки которого берём значение. */
  из: string
  /** Что взять из связанной карточки: код её поля. */
  взять: string
  /** Подставлять только в пустое поле. По умолчанию да — иначе затрём набранное руками. */
  толькоВПустое?: boolean
}

/** Своя проверка значения. Возвращает текст ошибки или null, если всё в порядке. */
export type Проверка = (значение: Значение, все: Значения) => string | null

export interface Поле {
  /** Код поля в записи. Совпадает с колонкой своей схемы. */
  код: string
  подпись: string
  вид: ВидПоля
  /** Код группы, внутри которой поле стоит. Группа задаёт вкладку и порядок. */
  группа: string
  /**
   * Подсказка под полем. Не украшение: «Курс к рублю на дату документа, а не сегодняшний»
   * — это единственное место, где человек узнает, почему поле вообще есть.
   */
  подсказка?: string
  /**
   * Обязательность. Функция — для условной: номер обязателен только при выпуске,
   * причина проигрыша — только при переходе в проигрыш.
   */
  обязательно?: boolean | ((все: Значения) => boolean)
  /** Почему поле обязательно именно сейчас. Показывается рядом с ошибкой. */
  почемуОбязательно?: string
  /** Поле только на чтение. Функция — если это зависит от состояния записи. */
  толькоЧтение?: boolean | ((все: Значения) => boolean)
  /** Почему нельзя править: «вычисляет база», «считает бизнес-процесс». */
  почемуНельзяПравить?: string
  /**
   * Варианты для вида `список`. Плоский набор: сюда же смотрят слой записи и сверка со
   * справочником базы, а они про соседние поля ничего не знают.
   */
  варианты?: Вариант[]
  /**
   * Список зависит от другого поля этой же карточки.
   *
   * ЗАЧЕМ. Код стадии сделки уникален ВНУТРИ воронки, а не вообще: в справочнике `stage` 92
   * строки и всего 38 разных кодов, `executing` — это шесть разных стадий шести воронок с
   * разными подписями. Показать все 38 одним списком значит дать выбрать стадию чужой
   * воронки, а слой записи найдёт её по коду и запишет молча — `WHERE code = %s` воронки не
   * знает. Поэтому набор сужается по выбранной воронке.
   *
   * ЧТО БУДЕТ, ЕСЛИ ПОЛЕ-ХОЗЯИН ЕЩЁ НЕ ЗАПОЛНЕНО. Возвращается плоский `варианты` — весь
   * справочник. Пустой список тут был бы хуже: он читался бы как «выбирать нечего».
   */
  вариантыПо?: { поле: string; наборы: Record<string, Вариант[]> }
  /** Сущность для вида `связь`. */
  связьС?: Сущность
  /** Код поля с валютой для вида `деньги`. По умолчанию валюта хранится внутри значения. */
  подсказкаЕдиницы?: string
  /** Своя проверка: ИНН, IMO, MMSI, почта. */
  проверка?: Проверка
  подстановка?: Подстановка
  /** Текст-заполнитель в пустом поле. */
  подсказкаВвода?: string
  /** Ограничение длины строки. */
  максДлина?: number
  /** Числовая колонка в таблицах и выравнивание вправо в карточке. */
  число?: boolean
}

// ─────────────────────────── группы, показатели, действия ───────────────────────────

/**
 * Группа полей внутри вкладки. Порядок групп в массиве — порядок на экране, и это
 * единственное место, где он задаётся.
 */
export interface Группа {
  код: string
  заголовок: string
  вкладка?: КодВкладки
  подсказка?: string
}

/** Ключевой показатель в шапке карточки: сумма, срок, долг, число позиций. */
export interface Показатель {
  код: string
  подпись: string
  значение: (з: Значения) => string
  /** Показатель тревожный (просрочка, провал) — плитка подсвечивается. */
  тревога?: (з: Значения) => boolean
}

/** Действие в шапке карточки помимо встроенных «Сохранить / Выпустить / Аннулировать». */
export interface Действие {
  код: string
  подпись: string
  главное?: boolean
  /** Почему действие сейчас недоступно. null — доступно. */
  почемуНельзя?: (з: Значения) => string | null
}

/**
 * Обратная связь: кто ссылается на нас. У компании это сделки, счета и договоры,
 * у судна — акты и проверки. Без обратных связей карточка остаётся анкетой.
 */
export interface ОбратнаяСвязь {
  код: string
  заголовок: string
  сущность: Сущность
  /** Поле связанной сущности, которое смотрит на нашу карточку. */
  поле: string
  колонки: { код: string; подпись: string; число?: boolean }[]
}

// ─────────────────────────── карточка целиком ───────────────────────────

export interface ОписаниеКарточки {
  сущность: Сущность
  /** Именительный единственного: «сделка». Для заголовков и пустых состояний. */
  один: string
  /** Винительный единственного: «сделку». Для «выберите сделку», «создать сделку». */
  винительный: string
  /** Множественное: «Сделки». Совпадает с пунктом меню. */
  много: string
  /** Документ (номер, выпуск, аннулирование) или справочная запись (архив). */
  документ: boolean
  /** Адрес карточки. Единственное место, где он собирается. */
  путь: (id: string) => string
  /** Заголовок карточки из значений. */
  заголовок: (з: Значения) => string
  показатели: Показатель[]
  группы: Группа[]
  поля: Поле[]
  обратные: ОбратнаяСвязь[]
  действия?: Действие[]
}

// ─────────────────────────── журнал изменений ───────────────────────────

export type ОперацияЖурнала = 'create' | 'update' | 'delete' | 'restore'

/**
 * Запись журнала: кто, когда, какое поле, из чего во что.
 *
 * `поле` пустое у операций создания и удаления — там менялась запись целиком.
 * `из`/`во` хранятся уже приведёнными к тексту: журнал читают люди, а не машина,
 * и восстанавливать по нему значение мы не обещаем.
 */
export interface ЗаписьЖурнала {
  ревизия: number
  когда: string
  кто: string
  операция: ОперацияЖурнала
  поле?: string
  подписьПоля?: string
  из?: string
  во?: string
  /** Обязателен для delete/restore: без причины запись нельзя ни удалить, ни вернуть. */
  причина?: string
  /** Откуда пришло изменение: рука человека, обмен, синк. */
  откуда?: 'ui' | 'api' | 'import' | 'job'
}

/** Событие ленты: письмо, звонок, комментарий, смена стадии. */
export interface Событие {
  id: string
  когда: string
  кто: string
  вид: 'комментарий' | 'письмо' | 'звонок' | 'стадия' | 'задача'
  текст: string
}

/** Запись, как её отдаёт источник. */
export interface ЗаписьКарточки {
  id: string
  /** Ревизия для оптимистической блокировки: форма отправляет её обратно. */
  ревизия: number
  состояние: Состояние
  значения: Значения
}
